// MST
import { types, flow, cast } from 'mobx-state-tree'

import {
  IHomeWelcomeGetAllAttrs,
  IHomeWelcomeGetAttrs,
  IHomeWelcomeSaveAttrs,
} from 'interfaces/back-office/content-management/home/welcome/home-welcome-view.interface'
import {
  IHomeWelcomeApiGetAllResponse,
  IHomeWelcomeApiGetResponse,
  IHomeWelcomeApiPaginatedResponse,
} from 'interfaces/back-office/content-management/home/welcome/home-welcome-api.interface'
import { IHomeWelcomeDetail } from 'interfaces/back-office/content-management/home/welcome'

// Extensions
import { withEnvironment } from 'models/extensions/with-environment'
import { withHelper_baseResponsePaginationTypes } from 'models/extensions/with-helper'

// Lodash
import omit from 'lodash/omit'
import { dateUtils_formatDate } from 'utils/common/date.utils'
import { IHomeNewsApiSaveResponse } from 'interfaces/back-office/content-management/home/news/home-news-api.interface'

// Home Welcome Props
const homeWelcomeProps = {
  number: types.maybeNull(types.number),
  id: types.maybeNull(types.number),
  description: types.maybeNull(types.string),
  status: types.maybeNull(types.boolean),
  createdAt: types.maybeNull(types.string),
  updatedAt: types.maybeNull(types.string),
}

// Home Welcome List Model
const HomeWelcomes = types
  .model('HomeWelcomes')
  .props(withHelper_baseResponsePaginationTypes(types.model('HomeWelcomeList').props(homeWelcomeProps)))

// Home Welcome Detail Model
const HomeWelcomeDetail = types.model('HomeWelcomeDetail').props(omit(homeWelcomeProps, ['number']))

const HomeWelcomeStoreModel = types
  .model('HomeWelcome')
  .props({
    homeWelcome_isLoading: types.optional(types.boolean, false),
    homeWelcome_isSaveLoading: types.optional(types.boolean, false),
    homeWelcome_homeWelcomes: types.optional(HomeWelcomes, {}),
    homeWelcome_detail: types.optional(HomeWelcomeDetail, {}),
  })
  .actions(self => ({
    homeWelcome_SET_LOADING: (payload: boolean): void => {
      self.homeWelcome_isLoading = payload
    },
    homeWelcome_SET_SAVE_LOADING: (payload: boolean): void => {
      self.homeWelcome_isSaveLoading = payload
    },
    homeWelcome_SET_HOME_WELCOMES: (payload: IHomeWelcomeApiPaginatedResponse): void => {
      const mapPayload = {
        ...payload,
        list: payload.list.map(list => {
          return {
            ...list,
            createdAt: dateUtils_formatDate(list.createdAt!, { tableFormat: true }),
            updatedAt: dateUtils_formatDate(list.updatedAt!, { tableFormat: true }),
          }
        }),
      }

      self.homeWelcome_homeWelcomes = cast(mapPayload)
    },
    homeWelcome_SET_HOME_WELCOME: (payload: IHomeWelcomeDetail): void => {
      self.homeWelcome_detail = cast(payload)
    },
    homeWelcome_CLEAR_HOME_WELCOMES: (): void => {
      self.homeWelcome_homeWelcomes = cast({})
    },
    homeWelcome_CLEAR_HOME_WELCOME: (): void => {
      self.homeWelcome_detail = cast({})
    },
  }))
  .extend(withEnvironment)
  .actions(self => ({
    /**
     * @description Fetch homeWelcomes
     *
     * @param {IHomeWelcomeGetAllAttrs} payload
     *
     * @return {any} any
     */
    homeWelcome_fetchHomeWelcomes: flow(function* (payload: IHomeWelcomeGetAllAttrs) {
      self.homeWelcome_SET_LOADING(true)

      const response: IHomeWelcomeApiGetAllResponse = yield self.environment.homeWelcomeApi.homeWelcome_getHomeWelcomes({
        body: { ...payload.body },
        requestConfig: { ...payload.requestConfig },
      })
      if (response.ok) self.homeWelcome_SET_HOME_WELCOMES(response.res.data)

      self.homeWelcome_SET_LOADING(false)
    }),

    /**
     * @description Fetch home welcome
     *
     * @param {IHomeWelcomeGetAttrs} payload
     *
     * @return {any} any
     */
    homeWelcome_fetchHomeWelcome: flow(function* (payload: IHomeWelcomeGetAttrs) {
      self.homeWelcome_SET_LOADING(true)

      const response: IHomeWelcomeApiGetResponse = yield self.environment.homeWelcomeApi.homeWelcome_getHomeWelcome({
        params: { id: payload.params.id },
        requestConfig: { ...payload.requestConfig },
      })
      if (response.ok) {
        self.homeWelcome_SET_HOME_WELCOME(response.res.data)

        // Do when response success
        if (payload?.onSuccess) payload.onSuccess()
      }

      if (!response.ok) {
        // Do when response error
        if (payload?.onError) payload.onError()
      }

      self.homeWelcome_SET_LOADING(false)
    }),

    /**
     * @description Save home welcome
     *
     * @param {IHomeWelcomeSaveAttrs} payload
     *
     * @return {any} any
     */
    homeWelcome_saveHomeWelcome: flow(function* (payload: IHomeWelcomeSaveAttrs) {
      self.homeWelcome_SET_SAVE_LOADING(true)

      const response: IHomeNewsApiSaveResponse = yield self.environment.homeWelcomeApi.homeWelcome_saveHomeWelcome({
        params: { ...payload.params },
        body: { ...payload.body },
        requestConfig: { ...payload.requestConfig },
      })
      if (response.ok) {
        // Do when response success
        if (payload?.onSuccess) payload.onSuccess()
      }

      self.homeWelcome_SET_SAVE_LOADING(false)
    }),
  }))

export const createHomeWelcomeStoreDefaultModel = () => types.optional(HomeWelcomeStoreModel, {})
